/**
 * Single family-facing money projection.
 * Inputs: trip document + optional household view. Outputs: remaining supplier
 * share, reimbursement and credit, never mixed across currencies and never
 * treating a reimbursed claim as still owed on a booking.
 */

import { bookingLedger, currentPaymentState, householdSupplierRemaining, tripLedger, type BookingLedger, type Claim, type UnallocatedCredit } from "./ledger.ts";
import { formatMinor } from "./money.ts";
import type { Booking, Household, SupplierPayment, SupplierRefund, TripDocument } from "./schema.ts";
import { bookingDisplayTitle, chronologicalSchedule, upcomingPayments, type UpcomingItem } from "./upcoming.ts";

export type HouseholdView = "all" | string;

export type AmountLine = {
  currency: string;
  amountMinor: number;
};

export type BookingMoneyRow = {
  bookingId: string;
  title: string;
  householdLabel: string | null;
  currency: string | null;
  originalMinor: number | null;
  remainingMinor: number;
  paid: boolean;
  cancelled: boolean;
  paymentState: ReturnType<typeof currentPaymentState>;
  missingCost: boolean;
};

export type BookingMoneyDetail = {
  booking: Booking;
  ledger: BookingLedger;
  originalCovered: Claim[];
  currentNet: Claim[];
  currentCredits: UnallocatedCredit[];
  payments: SupplierPayment[];
  refunds: SupplierRefund[];
  paymentState: ReturnType<typeof currentPaymentState>;
  historicalBaseline: string | null;
};

export type ReimbursementLine = {
  fromHouseholdId: string;
  toHouseholdId: string;
  fromLabel: string;
  toLabel: string;
  directionLabel: string;
  currency: string;
  amountMinor: number;
  bookings: Array<{ bookingId: string; title: string; amountMinor: number }>;
};

export type HouseholdMoneySummary = {
  view: HouseholdView;
  viewLabel: string;
  remainingSupplier: AmountLine[];
  reimbursementOwed: AmountLine[];
  reimbursementToLabel: string | null;
  reimbursementLines: ReimbursementLine[];
  creditDue: AmountLine[];
  creditFromLabel: string | null;
  creditLines: ReimbursementLine[];
  nextPayment: UpcomingItem | null;
  reimbursementBookings: Array<{ bookingId: string; title: string; currency: string; amountMinor: number }>;
};

export type TripMoneyView = {
  ledger: ReturnType<typeof tripLedger>;
  summary: HouseholdMoneySummary;
  schedule: ReturnType<typeof chronologicalSchedule>;
  bookingRows: BookingMoneyRow[];
  totals: { byCurrency: Record<string, number>; complete: false };
};

function linesFromRecord(record: Record<string, number> | undefined): AmountLine[] {
  return Object.entries(record ?? {})
    .filter(([, amount]) => amount !== 0)
    .map(([currency, amountMinor]) => ({ currency, amountMinor }));
}

export function amountsToSentence(lines: AmountLine[], empty = "Nothing"): string {
  if (lines.length === 0) return empty;
  return lines.map((line) => formatMinor(line.currency, line.amountMinor)).join(" and ");
}

export function presentBookingMoney(doc: TripDocument, bookingId: string): BookingMoneyDetail | null {
  const booking = doc.bookings.find((row) => row.id === bookingId);
  if (!booking) return null;
  const householdIds = doc.households.map((h) => h.id);
  const ledger = bookingLedger(booking, doc.supplierPayments, doc.supplierRefunds, householdIds);
  const trip = tripLedger(doc);
  return {
    booking,
    ledger,
    originalCovered: ledger.claims,
    currentNet: trip.claims,
    currentCredits: trip.unallocatedCredits,
    payments: doc.supplierPayments.filter((row) => row.bookingId === bookingId),
    refunds: doc.supplierRefunds.filter((row) => row.bookingId === bookingId),
    paymentState: currentPaymentState(ledger),
    historicalBaseline: booking.paymentBaseline
      ? `Reported unpaid on ${booking.paymentBaseline.asOf ?? "an earlier date"}`
      : null,
  };
}

function labelFor(households: Household[], id: string): string {
  return households.find((h) => h.id === id)?.label ?? id;
}

function groupClaims(
  claims: Claim[],
  households: Household[],
  doc: TripDocument,
  ledgers: BookingLedger[],
): ReimbursementLine[] {
  const map = new Map<string, ReimbursementLine>();
  for (const claim of claims) {
    const key = `${claim.fromHouseholdId}->${claim.toHouseholdId}:${claim.currency}`;
    const fromLabel = labelFor(households, claim.fromHouseholdId);
    const toLabel = labelFor(households, claim.toHouseholdId);
    const existing = map.get(key);
    if (existing) existing.amountMinor += claim.amountMinor;
    else {
      map.set(key, {
        fromHouseholdId: claim.fromHouseholdId,
        toHouseholdId: claim.toHouseholdId,
        fromLabel,
        toLabel,
        directionLabel: `${fromLabel} owes ${toLabel}`,
        currency: claim.currency,
        amountMinor: claim.amountMinor,
        bookings: [],
      });
    }
  }
  for (const row of ledgers) {
    const booking = doc.bookings.find((b) => b.id === row.bookingId);
    if (!booking) continue;
    for (const claim of row.claims) {
      const key = `${claim.fromHouseholdId}->${claim.toHouseholdId}:${claim.currency}`;
      const line = map.get(key);
      if (!line || claim.amountMinor === 0) continue;
      line.bookings.push({
        bookingId: row.bookingId,
        title: bookingDisplayTitle(booking, doc),
        amountMinor: claim.amountMinor,
      });
    }
  }
  return [...map.values()].filter((line) => line.amountMinor !== 0);
}

export function householdMoneySummary(doc: TripDocument, view: HouseholdView): HouseholdMoneySummary {
  const ledger = tripLedger(doc);
  const households = doc.households;
  const viewLabel = view === "all" ? "Everyone" : households.find((h) => h.id === view)?.label ?? view;
  const remainingSupplier = view === "all"
    ? linesFromRecord(ledger.supplierByCurrency)
    : linesFromRecord(ledger.remainingByHousehold[view]);

  const visibleClaims = view === "all"
    ? ledger.claims
    : ledger.claims.filter((claim) => claim.fromHouseholdId === view || claim.toHouseholdId === view);
  const reimbursementLines = groupClaims(
    view === "all" ? ledger.claims : ledger.claims.filter((claim) => claim.fromHouseholdId === view),
    households,
    doc,
    ledger.bookings,
  );
  const creditLines = groupClaims(
    view === "all"
      ? []
      : ledger.claims.filter((claim) => claim.toHouseholdId === view),
    households,
    doc,
    ledger.bookings,
  ).map((line) => ({
    ...line,
    directionLabel: `${line.fromLabel} owes ${line.toLabel}`,
  }));

  const reimbursementOwed: AmountLine[] = [];
  const creditDue: AmountLine[] = [];
  let reimbursementToLabel: string | null = null;
  let creditFromLabel: string | null = null;

  if (view === "all") {
    for (const line of reimbursementLines) {
      reimbursementOwed.push({ currency: line.currency, amountMinor: line.amountMinor });
      reimbursementToLabel = line.directionLabel;
    }
    for (const credit of ledger.unallocatedCredits) {
      creditDue.push({ currency: credit.currency, amountMinor: credit.amountMinor });
      creditFromLabel = `${labelFor(households, credit.toHouseholdId)} holds credit from ${labelFor(households, credit.fromHouseholdId)}`;
    }
  } else {
    for (const claim of visibleClaims) {
      if (claim.fromHouseholdId === view) {
        reimbursementOwed.push({ currency: claim.currency, amountMinor: claim.amountMinor });
        reimbursementToLabel = labelFor(households, claim.toHouseholdId);
      } else if (claim.toHouseholdId === view) {
        creditDue.push({ currency: claim.currency, amountMinor: claim.amountMinor });
        creditFromLabel = labelFor(households, claim.fromHouseholdId);
      }
    }
    for (const credit of ledger.unallocatedCredits) {
      if (credit.fromHouseholdId === view) {
        creditDue.push({ currency: credit.currency, amountMinor: credit.amountMinor });
        creditFromLabel = labelFor(households, credit.toHouseholdId);
      } else if (credit.toHouseholdId === view) {
        reimbursementOwed.push({ currency: credit.currency, amountMinor: credit.amountMinor });
        reimbursementToLabel = labelFor(households, credit.fromHouseholdId);
      }
    }
  }

  const schedule = chronologicalSchedule(
    upcomingPayments(doc).filter((item) => view === "all" || item.householdId === view),
  );
  const nextPayment = schedule.dated.find((item) => !item.paid) ?? null;

  const reimbursementBookings = reimbursementLines.flatMap((line) =>
    line.bookings.map((row) => ({
      bookingId: row.bookingId,
      title: row.title,
      currency: line.currency,
      amountMinor: row.amountMinor,
    })),
  );

  return {
    view,
    viewLabel,
    remainingSupplier,
    reimbursementOwed,
    reimbursementToLabel,
    reimbursementLines,
    creditDue,
    creditFromLabel,
    creditLines,
    nextPayment,
    reimbursementBookings,
  };
}

export function tripMoneyView(doc: TripDocument, view: HouseholdView): TripMoneyView {
  const ledger = tripLedger(doc);
  const summary = householdMoneySummary(doc, view);
  const upcoming = upcomingPayments(doc).filter((item) => view === "all" || item.householdId === view);
  const rows: BookingMoneyRow[] = ledger.bookings
    .filter((row) => {
      const booking = doc.bookings.find((b) => b.id === row.bookingId);
      if (!booking) return false;
      if (view === "all") return true;
      return booking.householdId === view || booking.allocations.some((a) => a.householdId === view);
    })
    .map((row) => {
      const booking = doc.bookings.find((b) => b.id === row.bookingId)!;
      const remainingMinor = view === "all"
        ? row.supplierRemaining
        : householdSupplierRemaining(row, view);
      const originalMinor = view === "all"
        ? row.total?.amountMinor ?? null
        : row.allocated[view] ?? row.total?.amountMinor ?? null;
      return {
        bookingId: row.bookingId,
        title: bookingDisplayTitle(booking, doc),
        householdLabel: booking.householdId
          ? doc.households.find((h) => h.id === booking.householdId)?.label ?? null
          : null,
        currency: row.currency,
        originalMinor,
        remainingMinor,
        paid: remainingMinor <= 0 && (originalMinor ?? 0) > 0,
        cancelled: row.cancelled,
        paymentState: currentPaymentState(row),
        missingCost: row.missingCost,
      };
    });

  const byCurrency: Record<string, number> = {};
  for (const booking of doc.bookings) {
    if (booking.bookingStatus === "cancelled" || !booking.cost) continue;
    byCurrency[booking.cost.currency] = (byCurrency[booking.cost.currency] ?? 0) + booking.cost.amountMinor;
  }

  return {
    ledger,
    summary,
    schedule: chronologicalSchedule(upcoming),
    bookingRows: rows,
    totals: { byCurrency, complete: false },
  };
}

export function nextExpectedPayment(doc: TripDocument, view: HouseholdView = "all"): UpcomingItem | null {
  return householdMoneySummary(doc, view).nextPayment;
}

export function moneyHref(path: string, view: HouseholdView): string {
  const url = new URL(path, "https://local.trip");
  if (view && view !== "all") url.searchParams.set("view", view);
  return `${url.pathname}${url.search}`;
}

export function parseHouseholdView(value: string | null | undefined, households: Household[]): HouseholdView {
  if (!value || value === "all") return "all";
  return households.some((h) => h.id === value) ? value : "all";
}
